import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import PrintQuotation from '@/components/print/PrintQuotation';
import { useToast } from '@/contexts/ToastContext';
import { type MockQuotation } from '@/mocks/billing';
import { quotationService } from '@/services/quotationService';
import { formatINR } from '@/utils/format';
import { asEntered } from '@/hooks/useUnits';

export default function QuotationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [printOpen, setPrintOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(true);
  const [qt, setQt] = useState<MockQuotation | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchQuotation = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await quotationService.getQuotation(id);
      setQt(data);
    } catch (err) {
      console.warn('Failed to fetch quotation details from API:', err);
      setQt(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchQuotation();
  }, [fetchQuotation]);

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64 text-slate-400">
          <div className="text-center">
            <i className="ri-loader-4-line text-3xl animate-spin block mb-2" />
            <p className="text-sm font-medium">Loading quotation details...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!qt) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64 text-slate-400">
          <div className="text-center">
            <i className="ri-file-text-line text-4xl block mb-2" />
            <p className="text-lg font-medium">Quotation not found</p>
            <button onClick={() => navigate('/sales/quotations')} className="mt-3 text-indigo-600 hover:underline text-sm cursor-pointer">Back to Quotations</button>
          </div>
        </div>
      </AppLayout>
    );
  }

  const today = new Date().toISOString().split('T')[0];
  const daysRemaining = Math.max(0, Math.ceil((Date.parse(qt.validUntil) - Date.parse(today)) / 86400000));
  const isExpiringSoon = qt.status === 'SENT' && daysRemaining <= 7 && daysRemaining > 0;

  const hasSO = Boolean((qt as any).convertedToSOId || (qt as any).resolved_so_id || (qt as any).salesOrderId || qt.status === 'CONVERTED_TO_SO');
  const soNumber = (qt as any).convertedToSONumber || (qt as any).salesOrderNo || (qt as any).salesOrderNumber || '';

  const hasPI = Boolean((qt as any).convertedToPIId || (qt as any).resolved_pi_id || (qt as any).proformaInvoiceId || qt.status === 'CONVERTED_TO_PI');
  const piNumber = (qt as any).convertedToPINumber || (qt as any).proformaInvoiceNo || (qt as any).proformaInvoiceNumber || '';

  const isNotDraftOrRejected = qt.status !== 'DRAFT' && qt.status !== 'REJECTED' && qt.status !== 'EXPIRED';

  const STATUS_STYLES: Record<string, string> = {
    DRAFT: 'bg-slate-100 text-slate-700 border-slate-300',
    SENT: 'bg-blue-100 text-blue-700 border-blue-300',
    ACCEPTED: 'bg-teal-100 text-teal-700 border-teal-300',
    REJECTED: 'bg-red-100 text-red-700 border-red-300',
    EXPIRED: 'bg-orange-100 text-orange-700 border-orange-300',
    CONVERTED_TO_SO: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    CONVERTED_TO_PI: 'bg-teal-100 text-teal-700 border-teal-300',
  };

  const handleSend = async () => {
    try {
      await quotationService.updateStatus(qt.id, 'SENT');
      toast.success(`Quotation ${qt.quotationNumber} marked as sent`);
      fetchQuotation();
    } catch {
      qt.status = 'SENT';
      setQt({ ...qt });
      toast.success(`Quotation ${qt.quotationNumber} marked as sent`);
    }
  };

  const handleConvertToSO = () => {
    if (!qt) return;
    const targetSoId = (qt as any).convertedToSOId || (qt as any).resolved_so_id || (qt as any).salesOrderId;
    if (hasSO && targetSoId) {
      navigate(`/sales/orders/${targetSoId}`);
      return;
    }

    navigate('/sales/orders/new', {
      state: {
        fromQuotation: {
          fromQuotation: true,
          quotationId: qt.id,
          quotationNumber: qt.quotationNumber,
          customerId: qt.customerId,
          customerName: qt.customerName,
          customerGSTIN: qt.customerGSTIN,
          warehouseId: qt.warehouseId,
          paymentTerms: qt.paymentTerms,
          deliveryTerms: qt.deliveryTerms,
          notes: qt.notes,
          items: (qt.items || []).map((i) => ({
            itemId: i.itemId,
            itemCode: (i as any).itemCode || '',
            itemName: i.itemName,
            hsnCode: i.hsnCode || '',
            description: i.description || '',
            qty: i.qty,
            unit: i.unit || 'Pcs',
            unitId: (i as any).unitId || '',
            enteredQty: (i as any).enteredQty ?? null,
            enteredUnitId: (i as any).enteredUnitId ?? null,
            enteredRate: (i as any).enteredRate ?? null,
            rate: i.rate,
            discount: (i as any).discountPercent ?? (i as any).discount ?? 0,
            taxRate: i.taxRate ?? 18,
          })),
        },
      },
    });
  };

  const handleReject = async () => {
    if (!qt) return;
    try {
      await quotationService.updateStatus(qt.id, 'REJECTED');
      toast.info(`Quotation ${qt.quotationNumber} rejected`);
      fetchQuotation();
    } catch {
      qt.status = 'REJECTED';
      setQt({ ...qt });
      toast.info(`Quotation ${qt.quotationNumber} rejected`);
    }
  };

  const handleCreatePI = () => {
    if (!qt) return;
    const targetPiId = (qt as any).convertedToPIId || (qt as any).resolved_pi_id || (qt as any).proformaInvoiceId;
    if (hasPI && targetPiId) {
      navigate(`/sales/proforma/${targetPiId}`);
      return;
    }

    navigate('/sales/proforma/new', {
      state: {
        fromQuotation: {
          quotationId: qt.id,
          quotationNumber: qt.quotationNumber,
          customerId: qt.customerId,
          customerName: qt.customerName,
          customerGSTIN: qt.customerGSTIN,
          warehouseId: qt.warehouseId,
          paymentTerms: qt.paymentTerms,
          deliveryTerms: qt.deliveryTerms,
          notes: qt.notes,
          items: (qt.items || []).map((i) => ({
            itemId: i.itemId,
            itemCode: (i as any).itemCode || '',
            itemName: i.itemName,
            hsnCode: i.hsnCode || '',
            description: i.description || '',
            qty: i.qty,
            unit: i.unit || 'Pcs',
            unitId: (i as any).unitId || '',
            enteredQty: (i as any).enteredQty ?? null,
            enteredUnitId: (i as any).enteredUnitId ?? null,
            enteredRate: (i as any).enteredRate ?? null,
            rate: i.rate,
            discount: (i as any).discountPercent ?? (i as any).discount ?? 0,
            taxRate: i.taxRate ?? 18,
          })),
        },
      },
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copied!'));
  };

  return (
    <AppLayout>
      <div className="flex flex-col h-full bg-[#f8fafc]">
        <div className="flex-1 overflow-y-auto p-6">
          {/* Back link */}
          <button
            onClick={() => navigate('/sales/quotations')}
            className="flex items-center gap-1 text-sm text-slate-500 hover:text-indigo-600 mb-4 cursor-pointer transition-colors"
          >
            <i className="ri-arrow-left-line" /> Back to Quotations
          </button>

          {/* Header */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <h1 className="text-xl font-bold text-slate-800 flex items-center gap-1.5">
                    {qt.quotationNumber}
                  </h1>
                  <button onClick={() => copyToClipboard(qt.quotationNumber)} className="w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 cursor-pointer transition-colors" title="Copy">
                    <i className="ri-file-copy-line text-sm" />
                  </button>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${STATUS_STYLES[qt.status]}`}>
                    {qt.status === 'SENT' ? 'Sent to Customer' : qt.status === 'CONVERTED_TO_SO' ? '→ SO Created' : qt.status === 'CONVERTED_TO_PI' ? '→ PI Created' : qt.status === 'DRAFT' ? 'Draft' : qt.status === 'ACCEPTED' ? 'Accepted' : qt.status === 'REJECTED' ? 'Rejected' : 'Expired'}
                  </span>
                </div>
                <div className="text-sm text-slate-600 space-y-0.5">
                  <p><span className="text-slate-400">Customer:</span> <strong>{qt.customerName}</strong> {qt.customerGSTIN && <span className="text-xs text-slate-400 ml-2">GSTIN: {qt.customerGSTIN}</span>}</p>
                  <p><span className="text-slate-400">Date:</span> {new Date(qt.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                  <p>
                    <span className="text-slate-400">Valid Until:</span>{' '}
                    <span className={isExpiringSoon ? 'text-amber-600 font-semibold' : 'text-slate-700'}>
                      {new Date(qt.validUntil).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                    {daysRemaining > 0 && (
                      <span className={`ml-1.5 text-xs ${isExpiringSoon ? 'text-amber-600 font-medium' : 'text-slate-400'}`}>
                        ({daysRemaining} day{daysRemaining > 1 ? 's' : ''} remaining)
                      </span>
                    )}
                  </p>
                  <p><span className="text-slate-400">Prepared by:</span> {qt.preparedBy || 'Admin User'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Proforma Invoice Action Button */}
                {hasPI ? (
                  <button onClick={handleCreatePI} className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 cursor-pointer whitespace-nowrap transition-colors">
                    <i className="ri-bill-line" /> View PI
                  </button>
                ) : (
                  isNotDraftOrRejected && (
                    <button onClick={handleCreatePI} className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 cursor-pointer whitespace-nowrap transition-colors">
                      <i className="ri-bill-line" /> Create Proforma Invoice
                    </button>
                  )
                )}

                {/* Sales Order Action Button */}
                {hasSO ? (
                  <button onClick={handleConvertToSO} className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 cursor-pointer whitespace-nowrap transition-colors">
                    <i className="ri-file-check-line" /> View SO
                  </button>
                ) : (
                  isNotDraftOrRejected && (
                    <button onClick={handleConvertToSO} className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer whitespace-nowrap transition-colors">
                      <i className="ri-file-copy-line" /> Convert to SO
                    </button>
                  )
                )}

                {qt.status === 'DRAFT' && (
                  <button onClick={handleSend} className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 cursor-pointer whitespace-nowrap transition-colors">
                    <i className="ri-send-plane-line" /> Send to Customer
                  </button>
                )}

                <button onClick={() => navigate(`/sales/quotations/new?edit=${qt.id}`)} className="flex items-center gap-1.5 h-9 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-indigo-600 cursor-pointer whitespace-nowrap transition-colors">
                  <i className="ri-edit-line" /> Edit
                </button>

                <button onClick={() => setPrintOpen(true)} className="flex items-center gap-1.5 h-9 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap transition-colors">
                  <i className="ri-printer-line" /> Print
                </button>

                {qt.status === 'DRAFT' && (
                  <button onClick={handleReject} className="flex items-center gap-1.5 h-9 px-4 rounded-lg border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50 cursor-pointer whitespace-nowrap transition-colors">
                    <i className="ri-close-circle-line" /> Cancel
                  </button>
                )}
                {qt.status === 'SENT' && (
                  <button onClick={handleReject} className="flex items-center gap-1.5 h-9 px-4 rounded-lg border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50 cursor-pointer whitespace-nowrap transition-colors">
                    <i className="ri-close-circle-line" /> Reject
                  </button>
                )}
              </div>
            </div>

            {/* Expiry Warning */}
            {isExpiringSoon && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2.5">
                <i className="ri-alert-line text-amber-600" />
                <span className="text-sm text-amber-700 font-medium">
                  This quotation expires in {daysRemaining} day{daysRemaining > 1 ? 's' : ''}. Send a reminder to the customer.
                </span>
              </div>
            )}

            {/* Conversion Banners */}
            {hasSO && (
              <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <i className="ri-check-double-line text-emerald-600" />
                  <span className="text-sm text-emerald-700 font-medium">
                    Sales Order {soNumber ? `(${soNumber}) ` : ''}has been created for this quotation.
                  </span>
                </div>
                {(qt as any).convertedToSOId && (
                  <button onClick={() => navigate(`/sales/orders/${(qt as any).convertedToSOId}`)} className="text-sm text-emerald-600 hover:underline cursor-pointer font-medium whitespace-nowrap">
                    View Sales Order {soNumber ? `(${soNumber}) ` : ''}→
                  </button>
                )}
              </div>
            )}

            {hasPI && (
              <div className="mt-4 p-3 bg-teal-50 border border-teal-200 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <i className="ri-bill-line text-teal-600" />
                  <span className="text-sm text-teal-700 font-medium">
                    Proforma Invoice {piNumber ? `(${piNumber}) ` : ''}has been created for this quotation.
                  </span>
                </div>
                {(qt as any).convertedToPIId && (
                  <button onClick={() => navigate(`/sales/proforma/${(qt as any).convertedToPIId}`)} className="text-sm text-teal-600 hover:underline cursor-pointer font-medium whitespace-nowrap">
                    View Proforma Invoice {piNumber ? `(${piNumber}) ` : ''}→
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Items Table */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-5">
            <div className="px-5 py-3 border-b border-slate-100">
              <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Items</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-center px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap w-10">#</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">Item</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">Description</th>
                    <th className="text-center px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">HSN</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">Qty</th>
                    <th className="text-center px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">Unit</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">Rate</th>
                    <th className="text-center px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">Disc%</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">Taxable</th>
                    <th className="text-center px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">GST%</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {qt.items.map((l) => asEntered(l)).map((item, idx) => (
                    <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="text-center px-4 py-3 text-slate-500 text-xs">{idx + 1}</td>
                      <td className="px-4 py-3 font-medium text-slate-800">{item.itemName}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{item.description || '—'}</td>
                      <td className="text-center px-4 py-3 text-xs text-slate-600">{item.hsnCode}</td>
                      <td className="text-right px-4 py-3 text-slate-700">{item.qty}</td>
                      <td className="text-center px-4 py-3 text-xs text-slate-500">{item.unit}</td>
                      <td className="text-right px-4 py-3 text-slate-700">{formatINR(item.rate)}</td>
                      <td className="text-center px-4 py-3 text-xs">
                        {item.discountPercent > 0 ? (
                          <span className="text-amber-600 font-medium">{item.discountPercent}%</span>
                        ) : '—'}
                      </td>
                      <td className="text-right px-4 py-3 text-slate-700">{formatINR(item.taxableAmount)}</td>
                      <td className="text-center px-4 py-3 text-xs text-slate-600">{item.taxRate}%</td>
                      <td className="text-right px-4 py-3 font-medium text-slate-800">{formatINR(item.totalAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="border-t border-slate-200 px-5 py-4 flex justify-end">
              <div className="w-72 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Subtotal</span>
                  <span className="font-medium text-slate-700">{formatINR(qt.subtotal)}</span>
                </div>
                {qt.discountAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Discount</span>
                    <span className="font-medium text-red-600">-{formatINR(qt.discountAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-500">Taxable Amount</span>
                  <span className="font-medium text-slate-700">{formatINR(qt.taxableAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Tax</span>
                  <span className="font-medium text-slate-700">{formatINR(qt.taxAmount)}</span>
                </div>
                <div className="border-t border-slate-200 pt-2 flex justify-between">
                  <span className="font-bold text-slate-800">Grand Total</span>
                  <span className="font-bold text-slate-800 text-base">{formatINR(qt.totalAmount)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Terms Section */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-5">
            <button
              onClick={() => setTermsOpen(!termsOpen)}
              className="w-full flex items-center justify-between px-5 py-3 border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
            >
              <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Terms &amp; Details</h2>
              <i className={`text-slate-400 text-sm transition-transform duration-200 ${termsOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
            </button>
            {termsOpen && (
              <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                {qt.paymentTerms && (
                  <div>
                    <div className="text-xs font-semibold text-slate-400 uppercase mb-1">Payment Terms</div>
                    <div className="text-sm text-slate-700">{qt.paymentTerms}</div>
                  </div>
                )}
                {qt.deliveryTerms && (
                  <div>
                    <div className="text-xs font-semibold text-slate-400 uppercase mb-1">Delivery Terms</div>
                    <div className="text-sm text-slate-700">{qt.deliveryTerms}</div>
                  </div>
                )}
                {qt.notes && (
                  <div className="md:col-span-2">
                    <div className="text-xs font-semibold text-slate-400 uppercase mb-1">Notes</div>
                    <div className="text-sm text-slate-700">{qt.notes}</div>
                  </div>
                )}
                {qt.termsAndConditions && (
                  <div className="md:col-span-2">
                    <div className="text-xs font-semibold text-slate-400 uppercase mb-1">Terms &amp; Conditions</div>
                    <div className="text-sm text-slate-700 whitespace-pre-line">{qt.termsAndConditions}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Print Modal */}
        {printOpen && (
          <PrintQuotation quotation={qt} onClose={() => setPrintOpen(false)} />
        )}
      </div>
    </AppLayout>
  );
}